import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { ParcelList } from './ParcelList';
import { CreateParcelForm } from './CreateParcelForm';
import { UpdateStatusModal } from './UpdateStatusModal';
import { LoginPage } from './LoginPage';
import { apiService } from '../../services/apiService';
import { authService } from '../../services/authService';
export const AdminDashboard = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
    const [activeTab, setActiveTab] = useState('parcels');
    const [parcels, setParcels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedParcel, setSelectedParcel] = useState(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    useEffect(() => {
        if (activeTab === 'parcels') {
            loadParcels();
        }
    }, [activeTab]);
    const loadParcels = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getAllParcels();
            if (response.success && response.data) {
                setParcels(response.data);
            }
        }
        catch (err) {
            setError(err.message || 'Failed to load parcels');
        }
        finally {
            setLoading(false);
        }
    };
    const handleParcelCreated = () => {
        setActiveTab('parcels');
        loadParcels();
    };
    const handleUpdateStatus = (parcel) => {
        setSelectedParcel(parcel);
        setShowUpdateModal(true);
    };
    const handleStatusUpdated = () => {
        setShowUpdateModal(false);
        setSelectedParcel(null);
        loadParcels();
    };
    const handleDeleteParcel = async (trackingNumber) => {
        if (!confirm('Are you sure you want to delete this parcel?'))
            return;
        try {
            await apiService.deleteParcel(trackingNumber);
            loadParcels();
        }
        catch (err) {
            alert(err.message || 'Failed to delete parcel');
        }
    };
    const handleTriggerAutoUpdate = async () => {
        setLoading(true);
        try {
            const response = await apiService.triggerAutoUpdate();
            alert(response.message || 'Auto-update triggered successfully');
            loadParcels();
        }
        catch (err) {
            alert(err.message || 'Failed to trigger auto-update');
        }
        finally {
            setLoading(false);
        }
    };
    const handleLoginSuccess = () => {
        setIsAuthenticated(true);
    };
    const handleLogout = () => {
        authService.logout();
        setIsAuthenticated(false);
    };
    // Show login page if not authenticated
    if (!isAuthenticated) {
        return _jsx(LoginPage, { onLoginSuccess: handleLoginSuccess });
    }
    return (_jsxs("div", { className: "min-h-screen bg-slate-50", children: [_jsx("header", { className: "bg-slate-900 text-white shadow-lg", children: _jsx("div", { className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6", children: _jsxs("div", { className: "flex justify-between items-center", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-bold", children: "Logistics Backoffice" }), _jsxs("p", { className: "text-slate-400 mt-1", children: ["Manage parcels and tracking", authService.getCurrentUser() && (_jsxs("span", { className: "ml-2", children: ["\u2022 Logged in as ", _jsx("span", { className: "text-white", children: authService.getCurrentUser()?.username })] }))] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: handleLogout, className: "px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-sm font-medium", children: "Logout" }), _jsx("a", { href: "/", className: "px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors text-sm font-medium", children: "Back to Website" })] })] }) }) }), _jsx("div", { className: "bg-white border-b border-slate-200", children: _jsx("div", { className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8", children: _jsxs("nav", { className: "flex space-x-8", children: [_jsx("button", { onClick: () => setActiveTab('parcels'), className: `py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'parcels'
                                    ? 'border-red-600 text-red-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`, children: "All Parcels" }), _jsx("button", { onClick: () => setActiveTab('create'), className: `py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'create'
                                    ? 'border-red-600 text-red-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`, children: "Create Parcel" })] }) }) }), _jsxs("main", { className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8", children: [error && (_jsx("div", { className: "mb-6 bg-red-50 border border-red-200 rounded-lg p-4", children: _jsx("p", { className: "text-red-800", children: error }) })), activeTab === 'parcels' && (_jsxs("div", { children: [_jsxs("div", { className: "mb-6 flex justify-between items-center", children: [_jsxs("h2", { className: "text-2xl font-bold text-slate-900", children: ["Parcel Management (", parcels.length, ")"] }), _jsxs("div", { className: "flex gap-3", children: [_jsx("button", { onClick: loadParcels, disabled: loading, className: "px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium", children: loading ? 'Refreshing...' : 'Refresh' }), _jsx("button", { onClick: handleTriggerAutoUpdate, disabled: loading, className: "px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium", children: "Trigger Auto-Update" })] })] }), loading ? (_jsx("div", { className: "flex justify-center items-center py-20", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" }) })) : (_jsx(ParcelList, { parcels: parcels, onUpdateStatus: handleUpdateStatus, onDelete: handleDeleteParcel }))] })), activeTab === 'create' && (_jsxs("div", { children: [_jsx("h2", { className: "text-2xl font-bold text-slate-900 mb-6", children: "Create New Parcel" }), _jsx(CreateParcelForm, { onSuccess: handleParcelCreated })] }))] }), showUpdateModal && selectedParcel && (_jsx(UpdateStatusModal, { parcel: selectedParcel, onClose: () => setShowUpdateModal(false), onSuccess: handleStatusUpdated }))] }));
};
